import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ValidationService } from '../../../../infrastructure/services/validation.service';
import { EditarConversacionService } from './editar-conversacion.service';
import { Conversacion } from '../../interfaces/conversacion';
import { ToastrService } from 'ngx-toastr';
import {
  PerfilConversacionAsignado,
  PerfilConversacionListable,
} from '../../interfaces/perfil-conversacion';
import { CompruebaConversacionTieneMesajesService } from '../../services/comprueba-conversacion-tiene-mesajes.service';
import { Carpeta } from '../../interfaces/carpeta';
import { zip } from 'rxjs';
import { ListarCarpetasService } from '../../services/listar-carpetas.service';
import { ObtenerPerfilesConversacionService } from '../../services/obtener-perfiles-conversacion.service';
import { ObtenerGrupoAsignadoService } from '../../services/obtener-grupo-asignado.service';
import { ModalService } from '../modal/modal.service';
import { ObtenerPerfilesAsignadosGrupoUsuariosService } from '../../services/obtener-perfiles-asignados-grupo-usuarios.service';
import { CustomSelectComponent } from '../custom-select/custom-select.component';
import { OptionItem } from '../../interfaces/option-item';

export interface EditarConversacionFG {
  asunto: string | null;
  mensaje_sistema: string | null;
  perfil_conversacion_id: string | null;
  modelo: string | null;
  carpeta_id: string | null;
  perfiles_limitados: boolean;
}

@Component({
  selector: 'app-modal-editar-conversacion',
  templateUrl: './modal-editar-conversacion.component.html',
  styleUrls: ['./modal-editar-conversacion.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalEditarConversacionComponent implements OnInit {
  public conversacion = {} as Conversacion;
  public editarConversacionForm = {} as FormGroup;
  public loading: boolean = false;
  public modelosLLMActivos!: OptionItem[];
  public perfilConversacionIdAsignadoPorGrupo: string | null | undefined;
  public perfilTieneModelo: boolean = true;
  public permiteOpcionPersonalizada: boolean = false;
  public conversacionTieneMensajes: boolean = true;
  @ViewChild('perfilConversacion')
  perfilConversacionSelect!: CustomSelectComponent;

  @Input()
  set modelosActivos(modelosActivos: OptionItem[]) {
    this.modelosLLMActivos = modelosActivos;
    this.compruebaConversacionTieneMensajes(modelosActivos);
  }

  public carpetas!: Carpeta[];
  public perfilesConversacion!:
    | PerfilConversacionListable[]
    | PerfilConversacionAsignado[];

  constructor(
    private formBuilder: FormBuilder,
    private editarConversacionService: EditarConversacionService,
    private validationService: ValidationService,
    private obtenerGrupoAsignadoService: ObtenerGrupoAsignadoService,
    private toastrService: ToastrService,
    private compruebaConversacionTieneMesajesService: CompruebaConversacionTieneMesajesService,
    private listarCarpetasService: ListarCarpetasService,
    private obtenerPerfilesConversacionService: ObtenerPerfilesConversacionService,
    private modalService: ModalService,
    private obtenerPerfilesAsignadosGrupoUsuariosService: ObtenerPerfilesAsignadosGrupoUsuariosService,
  ) {
    this.buildForm();
  }

  public ngOnInit(): void {
    this.modalService.setPrimaryButtonAction(
      this.editarConversacion.bind(this),
    );
  }

  public buildForm(): void {
    this.editarConversacionForm = this.formBuilder.group<EditarConversacionFG>({
      asunto: null,
      mensaje_sistema: null,
      perfil_conversacion_id: null,
      modelo: null,
      carpeta_id: null,
      perfiles_limitados: false,
    });
  }

  public editarConversacion(): void {
    this.loading = true;
    this.editarConversacionService
      .__invoke(this.conversacion.id, this.editarConversacionForm.value)
      .subscribe({
        next: (conversacion) => {
          this.loading = false;
          this.modalService.close(conversacion);
          this.buildForm();
          this.toastrService.success('Conversación editada con éxito');
        },
        error: (errorResponse) => {
          this.loading = false;
          this.validationService.validateForm(
            this.editarConversacionForm,
            errorResponse.error.errors,
          );
        },
      });
  }

  public setConversacion(conversacion: Conversacion): void {
    this.conversacion = conversacion;
    this.editarConversacionForm.patchValue({
      asunto: this.conversacion.asunto,
      mensaje_sistema: this.conversacion.mensajeSistema,
      modelo: this.conversacion.modelo,
      carpeta_id: this.conversacion.carpetaId,
      perfil_conversacion_id: this.conversacion.perfil_conversacion_id,
    });
    this.inicializar();
  }

  private compruebaConversacionTieneMensajes(
    modelosActivos: OptionItem[],
  ): void {
    this.compruebaConversacionTieneMesajesService
      .__invoke(this.conversacion.id)
      .subscribe({
        next: (conversacionTieneMensajes) => {
          modelosActivos.forEach((modeloActivo, index) => {
            this.conversacionTieneMensajes = conversacionTieneMensajes;
          });
        },
      });
  }

  protected inicializar(): void {
    this.obtenerGrupoAsignadoService.__invoke().subscribe({
      next: (grupo) => {
        this.obtenerPerfilesAsignadosGrupoUsuariosService
          .__invoke(grupo.id)
          .subscribe({
            next: (perfiles) => {
              this.perfilesConversacion = perfiles;
              this.onPerfilConversacion(
                this.editarConversacionForm.get('perfil_conversacion_id')
                  ?.value,
              );
              if (this.perfilesConversacion.length === 0) {
                this.permiteOpcionPersonalizada = true;
                this.obtenerPerfilesConversacionService.__invoke().subscribe({
                  next: (perfilesConversacion) => {
                    this.perfilesConversacion = perfilesConversacion;
                    this.onPerfilConversacion(
                      this.editarConversacionForm.get('perfil_conversacion_id')
                        ?.value,
                    );
                  },
                });
              } else {
                this.permiteOpcionPersonalizada = false;
                this.editarConversacionForm
                  .get('perfiles_limitados')
                  ?.setValue(true);
              }

              if (this.perfilesConversacion.length === 1) {
                this.editarConversacionForm.patchValue({
                  perfil_conversacion_id: this.perfilesConversacion[0].id,
                });
                this.perfilConversacionIdAsignadoPorGrupo =
                  this.perfilesConversacion[0].id;
              }
              this.obtenerDatosSelects();
            },
          });
      },
    });
  }

  private obtenerDatosSelects(): void {
    zip(this.listarCarpetasService.__invoke()).subscribe({
      next: ([carpetas]) => {
        this.carpetas = carpetas;
      },
    });
  }

  public dismiss(): void {
    this.modalService.dismiss();
  }

  private onPerfilConversacion(perfilId: string | null): void {
    const selectedPerfil = this.perfilesConversacion.find(
      (perfil) => perfil.id === perfilId,
    );
    if (selectedPerfil && 'modelo' in selectedPerfil && selectedPerfil.modelo) {
      this.editarConversacionForm
        .get('modelo')
        ?.setValue(selectedPerfil.modelo);
      this.perfilTieneModelo = true;
    } else {
      this.perfilTieneModelo = false;
    }
  }
}
